<script lang="ts" setup>
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {
  computed,
  onBeforeMount,
  ref,
} from 'vue';
import {getRandomId, isMultiChoiceSelectionValid, multiChoiceValidationMessage} from '@/ts/utils/pure-functions';
import type {
  Option,
  QuestionMultiChoice,
} from '@/ts/types/dto/interventions-dto';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useI18n} from 'vue-i18n';
import FormRequired from '@/vue/atoms/form-required.vue';
import AriaListBox from '@/vue/molecules/aria-list-box.vue';
import type {ListBoxOption} from '@/ts/types/component/aria-list-box.type';
import AnswerOptionChip from '@/vue/molecules/answer-option-chip.vue';
import AnswerOptionDefault from '@/vue/molecules/answer-option-default.vue';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import CustomAnswerSuggestions from '@/vue/molecules/components/question/custom-answer-suggestions.vue';

const model = defineModel<UuidDTO[]>();
const customAnswerModel = defineModel<string>('customAnswer');
const props = defineProps<{
  question: QuestionMultiChoice;
  theme: 'chips' | 'list-question-in-col' | 'list-question-in-row';
}>();

const logger = useLogger();

const {t: $t} = useI18n();

const emit = defineEmits<{
  readyToGoNext: [],
}>();

const options = computed<Option[]>(() => props.question.multiChoice.options);

const isSingleChoice = computed(() => {
  return props.question.multiChoice.maxOptions === 1;
});

onBeforeMount(() => {
  logger.debug(`onBeforeMount ${props.question.contentId}`)();
  if (model.value && model.value.length > 0) {
    logger.trace(`Value of ${props.question.contentId} already set`)();
  } else if (props.question.multiChoiceSelectedIds) {
    logger.trace(`emit ${props.question.contentId} => ${props.question.multiChoiceSelectedIds.join(',')}`)();
    model.value = props.question.multiChoiceSelectedIds;
  } else if (!model.value) {
    logger.trace(`emit ${props.question.contentId} => []`)();
    model.value = [];
  }
});

const customAnswer = computed({
  get: () => customAnswerModel.value ?? '',
  set: (value: string) => { customAnswerModel.value = value; },
});

// Check if any selected options need custom answers
const shouldShowCustomInput = computed(() => {
  if (!model.value) {return false;}
  return model.value.some(optionId => {
    const option = options.value.find(opt => opt.id === optionId);
    return option?.needsCustomAnswer;
  });
});

const isValid = computed(() => isMultiChoiceSelectionValid(model.value, props.question.multiChoice, customAnswer.value));

const validationMessage = computed(() => multiChoiceValidationMessage(props.question.multiChoice));

const modelValue = computed({
  get():string[] {
    return model.value as string[];
  },
  set(val: string[]): void {
    // In single choice mode, clear custom input when selecting regular option
    if (isSingleChoice.value && val.length > 0) {
      customAnswer.value = '';
    }
    
    // Clear custom answer if no options requiring custom answers are selected
    const hasSelectedOptionsNeedingCustomAnswers = val.some(optionId => {
      const option = options.value.find(opt => opt.id === optionId);
      return option?.needsCustomAnswer;
    });
    
    if (!hasSelectedOptionsNeedingCustomAnswers) {
      customAnswer.value = '';
    }
    
    // Set the selection
    model.value = val as UuidDTO[];
    
    // Only emit readyToGoNext if it's single choice, has selection, and doesn't need custom answer
    if (isSingleChoice.value && val.length > 0) {
      const selectedOption = options.value.find(opt => val.includes(opt.id));
      if (!selectedOption?.needsCustomAnswer) {
        emit('readyToGoNext');
      }
    }
  },
});

const listBoxOptions = computed((): ListBoxOption[] => {
  return options.value.map((option): ListBoxOption => {
    return {
      id: option.id as string,
      text: option.text,
    };
  });
});


const listLabeledById = `question-${getRandomId()}`;

const customInputRef = ref<HTMLInputElement>();

const onCustomAnswerChange = (): void => {
  // Never auto-advance when custom answers are involved - user must manually continue
  // Note: We don't clear selections when typing custom answers since the input should remain visible
};

const onCustomAnswerSelect = (selectedAnswer: string): void => {
  customAnswer.value = selectedAnswer;
};

// Reference to suggestions component for keyboard navigation
const suggestionsRef = ref();

const handleInputKeydown = (event: KeyboardEvent): void => {
  // Only handle navigation keys when suggestions are visible
  if (!shouldShowCustomInput.value) {return;}
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      // Focus first suggestion
      if (suggestionsRef.value) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        suggestionsRef.value.focusIndex(0);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        suggestionsRef.value.containerRef?.focus();
      }
      break;
    case 'Escape':
      // Clear custom input
      customAnswer.value = '';
      break;
    default:
      // No action needed for other keys
      break;
  }
};

// Animation handlers with spring curves - simplified to avoid height conflicts
const onEnter = (el: Element): void => {
  const element = el as HTMLElement;
  const springEnter = getComputedStyle(element).getPropertyValue('--spring-enter');
  
  element.style.opacity = '0';
  element.style.transform = 'translateY(-10px) scaleY(0.8)';
  
  // Force reflow
  void element.offsetHeight;
  
  // Animate with spring curve
  element.style.transition = `opacity 0.25s ease-out, transform 0.35s ${springEnter}`;
  element.style.opacity = '1';
  element.style.transform = 'translateY(0) scaleY(1)';
  
  // Clear inline styles after animation completes
  setTimeout(() => {
    element.style.transition = '';
    element.style.transform = '';
  }, 350);
};

const onLeave = (el: Element): void => {
  const element = el as HTMLElement;
  const springLeave = getComputedStyle(element).getPropertyValue('--spring-leave');
  
  element.style.transition = `opacity 0.2s ease-in, transform 0.2s ${springLeave}`;
  element.style.opacity = '0';
  element.style.transform = 'translateY(-10px) scaleY(0.8)';
};


</script>
<template>
  <div class="question-grid-select">
    <div :class="theme">
      <div v-if="theme === 'list-question-in-row'" class="question-group">
        <badge-pill theme="blue" :label="$t('question')"/>
        <div :id="listLabeledById" v-safe-html="question.name" class="question"/>
      </div>
      <div v-else :id="listLabeledById" v-safe-html="question.name" class="question"/>

      <div class="answer">
        <aria-list-box
          v-model="modelValue"
          :options="listBoxOptions"
          :labeled-by="listLabeledById"
          :disable-default-focus-visible="true"
          :orientation="(theme === 'list-question-in-col' || theme === 'list-question-in-row') ? 'vertical' : 'ambiguous'"
          :maximum-selectable="question.multiChoice.maxOptions"
          class="list-box"
          :class="{
            'list-box--chips': theme === 'chips',
            'list-box--list': (theme === 'list-question-in-col' || theme === 'list-question-in-row'),
          }"
        >
          <template #item="templateProps">
            <answer-option-chip
              v-if="theme === 'chips'"
              :label="templateProps.option.text"
              :is-selected="templateProps.isSelected"
              :is-active="templateProps.isActive"
              :mood="'neutral'"
            />
            <answer-option-default
              v-else
              :label="templateProps.option.text"
              :is-selected="templateProps.isSelected"
              :is-active="templateProps.isActive"
            />
          </template>
        </aria-list-box>

        <!-- Custom answer input shown when any selected option needs custom answer -->
        <Transition
          name="custom-input"
          @enter="onEnter"
          @leave="onLeave"
        >
          <div v-if="shouldShowCustomInput" class="custom-input-container">
            <q-input
              ref="customInputRef"
              v-model="customAnswer"
              class="custom-input"
              type="textarea"
              :placeholder="$t('specify_your_answer')"
              borderless
              :aria-describedby="`suggestions-${question.contentId}`"
              @input="onCustomAnswerChange"
              @keydown="handleInputKeydown"
            />
            <custom-answer-suggestions
              :id="`suggestions-${question.contentId}`"
              ref="suggestionsRef"
              :question-id="question.contentId"
              :query="customAnswer"
              class="custom-suggestions"
              @select="onCustomAnswerSelect"
            />
          </div>
        </Transition>

        <form-required
          :id="`question-grid-${question.contentId}`"
          :valid="isValid"
          :message="$t(validationMessage.key, validationMessage.named)"
        />
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
@mixin question-grid-select-broad {
  @container question-grid-select (min-width: 599px) {
    @content;
  }
}

.question-grid-select {
  container-type: inline-size;
  container-name: question-grid-select;
  
  // Spring animation curves
  --spring-enter: cubic-bezier(0.68, -0.55, 0.265, 1.55);
  --spring-leave: cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.question {
  flex: 1;
  margin-bottom: var(--Spacing-spacing-5);
  @include font-display-3;
}

.answer {
  flex: 1;
}

.list-box {
  display: flex;
  gap: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-2) 0;

  &--chips {
    flex-wrap: wrap;
  }

  &--list {
    flex-direction: column;
  }
}

.list-question-in-col {
  display: flex;
  flex-direction: column;

  @include question-grid-select-broad {
    flex-direction: row;
  }

  .question {
    padding: 0 var(--Spacing-spacing-6) 0 0;
    margin-bottom: var(--Spacing-spacing-2);
    @include font-display-3; 

    @include question-grid-select-broad {
      align-self: center;
      margin-bottom: var(--Spacing-spacing-5);
      @include font-display-3;
    }
  }
}

.list-question-in-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-7);

  .question-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--Spacing-spacing-4);
    width: 100%;
  }

  .question {
    margin: 0;
    max-width: 1000px;
    text-align: center;

    :deep(p) {
      margin: 0;
    }
  }

  .answer {
    max-width: 800px;
    width: 100%;
  }

  .list-box--list {
    gap: 8px;
    padding: 0;
  }
}

.custom-input-container {
  margin-top: var(--Spacing-spacing-3);
  overflow: hidden;
  transform-origin: top;
  border-radius: var(--Border-Radius-3);
  border: 1px solid var(--Neutral-UI-neutral-ui-30);
  background: var(--Neutral-UI-neutral-ui-0);
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
}

.custom-input {
  &:deep(textarea) {
    padding: var(--Spacing-spacing-4);
    background: transparent;
    color: var(--Neutral-UI-neutral-ui-100);
    border: none;
    border-radius: var(--Border-Radius-3) var(--Border-Radius-3) 0 0;
    height: 100px;
    transition: all 0.2s ease;
    width: 100%;
    resize: none;
    
    &:focus {
      outline: none;
    }
  }
  
  // When suggestions are showing, remove bottom border radius from textarea
  &:has(+ .custom-suggestions .suggestions-container) {
    &:deep(textarea) {
      border-radius: var(--Border-Radius-3) var(--Border-Radius-3) 0 0;
    }
  }
}

// Vue transition classes for custom input with spring curves
.custom-input-enter-active {
  transition: opacity 0.25s ease-out, transform 0.35s var(--spring-enter);
}

.custom-input-leave-active {
  transition: opacity 0.2s ease-in, transform 0.2s var(--spring-leave);
}

.custom-input-enter-from {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.8);
}

.custom-input-leave-to {
  opacity: 0;
  transform: translateY(-10px) scaleY(0.8);
}

.custom-input-enter-to,
.custom-input-leave-from {
  opacity: 1;
  transform: translateY(0) scaleY(1);
}

</style>